import { computed, effect, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { APP_TITLE } from '../constants/app.constants';
import { NAVIGATION_ITEMS, NavigationItem } from '../constants/navigation.constants';
import { UserPermission } from '../constants/permission.constants.enum';
import { HelpDisplayService } from './help-display.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {

  showAllPermission = [
    UserPermission.MedNecScreen_ISAdmin,
    UserPermission.MedNecScreen_Quorum_ISAdmin
  ];

  navItems: Signal<NavigationItem[]>;

  flattenedNavItems: NavigationItem[] = [];

  navItems$: Observable<NavigationItem[]>;

  currentNavItem: WritableSignal<NavigationItem | null> = signal(null);

  unauthorizedNavItem: WritableSignal<NavigationItem | null> = signal(null);

  pageTitle = signal('');

  constructor(
    private user: UserService,
    private titleService: Title,
    private router: Router,
    private help: HelpDisplayService
  ) {
    this.navItems = computed(() => {
      const permissions = this.user.permissions();
      const items = this.configureNavItems(NAVIGATION_ITEMS, permissions);
      return permissions.length ? items : [];
    });

    this.navItems$ = toObservable(this.navItems).pipe(
      filter(items => items.length > 0)
    );

    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
    ).subscribe(this.setPageTitleFromNavEndEvent);

    effect(() => {
      const helpIndex = this.currentNavItem()?.helpIndex ?? -1;
      this.help.id.set(helpIndex);
    });
  }

  setPageTitleFromNavEndEvent = (e: NavigationEnd): void => {
    const path = e.urlAfterRedirects.slice(1);
    this.currentNavItem.set(this.findNavItemForPath(this.navItems(), path));
    const env = environment.environmentLabel !== 'prod' ? ' (' + environment.environmentLabel.toLocaleUpperCase() + ')' : '';
    const page = this.currentNavItem() ? `${this.currentNavItem()?.pageTitle ?? ''} | ` : '';
    const title = `${page}${APP_TITLE}${env}`;
    this.titleService.setTitle(title);

    const permissions = this.user.permissions();
    if(path === 'administration/users' &&
        permissions.includes(UserPermission.MedNecScreen_Admin) &&
        !permissions.includes(UserPermission.MedNecScreen_ISAdmin)) {
            this.pageTitle.set('View Users');
          } else {
              this.pageTitle.set(this.currentNavItem()?.title ?? '');
          }
  }

  configureNavItems(items: NavigationItem[], permissions: UserPermission[]): NavigationItem[] {
    this.flattenedNavItems = [];
    const setVisibility = (item: NavigationItem) => {
        item.visible = Array.isArray(item.visibleTo)
          ? this.hasAnyPermission(item.visibleTo)
          : typeof item.visible !== 'undefined' ? item.visible : true;
          addToFlattenedNav(item);
        return item;
    }
    const addToFlattenedNav = (item: NavigationItem): void => {
      if (item.visible && !item.separator && !Array.isArray(item.items)) this.flattenedNavItems.push(item);
    }
    const itemsWithSeparators: NavigationItem[] = []
    items.map(item => {
        setVisibility(item);
        if (Array.isArray(item.items)) {
            item.items = item.items.map(setVisibility)
        }
        itemsWithSeparators.push(item);
        if (Array.isArray(item.items) && item.items.filter(item => item.visible).length) {
          itemsWithSeparators.push({
            separator: true
          })
        }
    });
    return itemsWithSeparators;
  }

  hasAnyPermission = (itemPermissions: UserPermission[]): boolean => {
    let allowed = false;
    if (this.user.permissions().some(p => this.showAllPermission.includes(p))) {
      return true;
    }
    itemPermissions.map((itemPermission: UserPermission) => {
        if (this.user.permissions().includes(itemPermission)) allowed = true;
    });
    return allowed;
  }

  findNavItemForPath = (items: NavigationItem[], path?: string): NavigationItem | null => {
    let match: NavigationItem | null = null;
    items.some(item => {
      if (item.routerLink === path) {
        match = item;
        return true;
      } else if (Array.isArray(item.items)) {
        item.items.some(childItem => {
          if (childItem.routerLink === path) {
            match = childItem;
            return true;
          }
          return false;
        });
      }
      return false;
    });
    return match;
  }
}
